import * as t from 'io-ts';
import { uniqWith, isEqual } from 'lodash-es';
import { fromEnum, Signal } from '@bayer-int/portletce-shared';

import type { ViewId, ViewWithReference } from './view';
import { View } from './view';
import type { SettingValueMap } from './settings';
import { Settings, SettingDefinition } from './settings';
import type { ListenToDefinitions, ListenToDefinitionsPerView } from './listenTo';
import { ListenTo } from './listenTo';
import { PortletQuery } from './query';
import type { TitleAlign } from './settings/settingDefinitions/titleAlignSetting';
import { ViewHelper } from './viewHelper';

const SpecificationRequiredC = t.type({
    defaultViewId: t.number,
    views: t.array(t.union([View.codec, View.withRefCodec])),
    settings: Settings.codec,
});

const SpecificationOptionalC = t.partial({
    query: PortletQuery.codec,
    signals: t.array(Signal.codec),
    listenTo: t.array(ListenTo.codec),
    supportContentArea: t.string,
    description: t.string,
    tags: t.array(t.string),
    displayName: t.string,
    infoUrl: t.string,
    performanceThreshold: t.number,
    allowTelemetry: t.boolean,
    customErrorMessages: t.record(t.string, t.type({ title: t.string, message: t.string })),
});

const SpecificationC = t.intersection(
    [SpecificationRequiredC, SpecificationOptionalC],
    'Specification',
);

export type Specification = t.TypeOf<typeof SpecificationC>;

export type SpecificationId = string;

export enum SpecStatus {
    DEV = 'dev',
    REVIEW = 'review',
    RELEASED = 'released',
    LATEST = 'latest',
}

export const Specification = {
    codec: SpecificationC,
    requiredCodec: SpecificationRequiredC,
    optionalCodec: SpecificationOptionalC,
    specStatusCodec: fromEnum<SpecStatus>(SpecStatus),

    default: (): Specification => ({
        defaultViewId: View.DEFAULT_VIEW_ID,
        settings: {},
        views: [View.default()],
    }),

    defaultSettingValueMap: (): Partial<SettingValueMap> =>
        SettingDefinition.toValueMap(
            // using INVALID_VIEW_ID as default view id, so that when comparing
            // `defaultSettingValueMap` with actual settings it will always show up as a difference
            // (required for proper initialization, e.g. in SettingService.reloadSettingDefinitionOnViewChange)
            { ...Specification.default(), defaultViewId: View.INVALID_VIEW_ID },
            null,
        ),

    renderReferencedView: (view: View | ViewWithReference, config: Specification): View => {
        if (!View.isReferenceView(view)) return view;

        const referencedView = Specification.getViewById(config, view.$ref);

        return View.merge(view, referencedView || View.default());
    },

    getDefaultViewId: (spec: Specification): ViewId => spec.defaultViewId,

    getViewById: ViewHelper.getViewById,

    getDefaultView: (spec: Specification): View | null => {
        const defaultViewId = Specification.getDefaultViewId(spec);
        return ViewHelper.getViewById(spec, defaultViewId);
    },

    getTitle: (currentSettings: Partial<SettingValueMap>): string => {
        return currentSettings.TITLE || '';
    },

    getSubtitle: (currentSettings: Partial<SettingValueMap>): string => {
        return currentSettings.SUBTITLE || '';
    },

    getShortcutsColor: (currentSettings: Partial<SettingValueMap>): string => {
        return currentSettings.SHORTCUTS_COLOR || '';
    },

    getTitleAlign: (currentSettings: Partial<SettingValueMap>): TitleAlign => {
        return currentSettings.TITLE_ALIGN || 'center';
    },

    getListOfViews: (spec: Specification): Array<{ label: string; id: ViewId }> =>
        spec.views.map(({ id, label }) => ({ id, label })),

    getRenderedViews: (spec: Specification): View[] =>
        spec.views.map(view => Specification.renderReferencedView(view, spec)),

    getObservedFilters: (spec: Specification): ListenToDefinitions =>
        uniqWith(
            [
                ...(spec.listenTo || []),
                ...Specification.getRenderedViews(spec).flatMap(View.getObservedFilters),
            ],
            isEqual,
        ),

    getObservedFiltersPerView: (spec: Specification): ListenToDefinitionsPerView => [
        ...(spec.listenTo || []).map(definition => ({ viewId: null, definition })),
        ...Specification.getRenderedViews(spec).flatMap(view =>
            View.getObservedFilters(view).map(definition => ({ viewId: view.id, definition })),
        ),
    ],

    getSettingValues: (spec: Specification, currentView?: View): Partial<SettingValueMap> => {
        const view = currentView || Specification.getDefaultView(spec);
        const specSettings = Settings.mergeSpecSettings(spec.settings, view?.settings);

        return SettingDefinition.toValueMap(spec, view, specSettings);
    },

    getCustomErrorMessages: (
        spec: Specification,
    ): Record<string, { title: string; message: string }> => spec.customErrorMessages || {},
};
